import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import UserAvatar from '../common/UserAvatar';
import axios from 'axios';
import './Messages.css';

// Determine API base URL
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function Messages() {
  const { currentUser, currentRole } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [availableUsers, setAvailableUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('__ALL__');
  const [jobCategoryFilter, setJobCategoryFilter] = useState('__ALL__');
  const [dateFilter, setDateFilter] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const jobDetailsRef = useRef(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser) return;
      try {
        // Read custom claims directly from ID token
        let tr = await currentUser.getIdTokenResult();
        const hasAdminFromClaims = (() => {
          const claims = tr?.claims || {};
          const roles = claims.roles;
          if (Array.isArray(roles)) {
            return roles.map(String).map(r => r.toLowerCase()).includes('admin');
          }
          if (typeof roles === 'string') {
            return roles.toLowerCase() === 'admin';
          }
          return !!claims.admin;
        })();
        if (!hasAdminFromClaims) {
          // Force refresh in case claims were just updated
          tr = await currentUser.getIdTokenResult(true);
          const claims = tr?.claims || {};
          const roles = claims.roles;
          const refreshedAdmin =
            (Array.isArray(roles) && roles.map(String).map(r => r.toLowerCase()).includes('admin')) ||
            (typeof roles === 'string' && roles.toLowerCase() === 'admin') ||
            !!claims.admin;
          setIsAdmin(refreshedAdmin);
        } else {
          setIsAdmin(hasAdminFromClaims);
        }
      } catch (e) {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [currentUser]);


  const loadAvailableUsers = async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get(`${API_BASE_URL}/api/chat/available-users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setAvailableUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading available users:', error);
      setAvailableUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableJobs = async () => {
    if (!currentUser || currentRole !== 'freelancer') return;
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get(`${API_BASE_URL}/api/jobs?role=freelancer`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data && response.data.jobs) {
        setAvailableJobs(response.data.jobs || []);
      }
    } catch (error) {
      console.error('Error loading available jobs:', error);
      setAvailableJobs([]);
    }
  };

  useEffect(() => {
    if (!currentUser) return;
    loadAvailableUsers();
    if (currentRole === 'freelancer') {
      loadAvailableJobs();
    }

    // Set up polling to refresh users/jobs periodically
    const pollInterval = setInterval(() => {
      loadAvailableUsers();
      if (currentRole === 'freelancer') {
        loadAvailableJobs();
      }
    }, 5000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [currentUser, currentRole]);

  // Filter users based on search and filters
  useEffect(() => {
    let filtered = [...availableUsers];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user => {
        const displayName = user.displayName || user.email?.split('@')[0] || '';
        const email = user.email || '';
        const businessType = user.businessType || '';
        return displayName.toLowerCase().includes(query) ||
               email.toLowerCase().includes(query) ||
               businessType.toLowerCase().includes(query);
      });
    }

    // Apply category filter
    if (categoryFilter !== '__ALL__') {
      filtered = filtered.filter(user => {
        return user.businessType === categoryFilter;
      });
    }

    // Apply date filter (filter by start date)
    if (dateFilter) {
      filtered = filtered.filter(user => {
        return user.jobStartDate === dateFilter;
      });
    }

    setFilteredUsers(filtered);
  }, [availableUsers, searchQuery, categoryFilter, dateFilter]);

  // Filter jobs based on search and filters
  useEffect(() => {
    let filtered = [...availableJobs];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(job => {
        const title = job.title || '';
        const description = job.description || '';
        const location = job.location || '';
        return title.toLowerCase().includes(query) ||
               description.toLowerCase().includes(query) ||
               location.toLowerCase().includes(query);
      });
    }

    // Apply category filter
    if (jobCategoryFilter !== '__ALL__') {
      filtered = filtered.filter(job => {
        if (jobCategoryFilter === '__OPEN_TO_ALL__') {
          return job.open_to_all === true;
        }
        return job.category === jobCategoryFilter;
      });
    }

    // Apply date filter (filter by start date)
    if (dateFilter) {
      filtered = filtered.filter(job => {
        return job.start_date === dateFilter;
      });
    }

    setFilteredJobs(filtered);
  }, [availableJobs, searchQuery, jobCategoryFilter, dateFilter]);

  // Validate selected job - clear if it's no longer available or doesn't match filters
  useEffect(() => {
    if (currentRole === 'freelancer' && selectedJob) {
      // Check if the job still exists in the filtered jobs list
      const jobStillExists = filteredJobs.some(job => job.id === selectedJob.id);

      // Also check if job is still in availableJobs (before filtering) and is open
      const jobStillAvailable = availableJobs.some(job =>
        job.id === selectedJob.id && (job.status || 'open').toLowerCase() === 'open'
      );

      if (!jobStillExists || !jobStillAvailable) {
        setSelectedJob(null);
      }
    }
  }, [filteredJobs, availableJobs, selectedJob, currentRole]);

  // Get unique categories from users
  const categories = ['__ALL__', ...new Set(availableUsers.map(u => u.businessType).filter(Boolean))];

  // Get unique job categories
  const jobCategories = ['__ALL__', '__OPEN_TO_ALL__', ...new Set(availableJobs.map(j => j.category).filter(Boolean))];

  const handleUserClick = (user) => {
    setSelectedUser(user);
  };

  const handleChatClick = async () => {
    if (!selectedUser || !currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await axios.post(`${API_BASE_URL}/api/chat/conversation`, {
        otherUserId: selectedUser.uid
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Navigate to chat page with conversation ID
      navigate(`/chat?conversationId=${response.data.conversationId}`);
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Failed to start chat. Please try again.');
    }
  };

  const handleApplyToJob = async () => {
    if (!selectedJob || !currentUser || !selectedJob.business_id) return;

    // Validate that the job is still available and open
    const jobStillAvailable = availableJobs.some(job =>
      job.id === selectedJob.id && (job.status || 'open').toLowerCase() === 'open'
    );

    if (!jobStillAvailable) {
      alert(t('jobs.jobNoLongerAvailable') || 'This job is no longer available.');
      setSelectedJob(null);
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await axios.post(`${API_BASE_URL}/api/chat/conversation`, {
        otherUserId: selectedJob.business_id,
        jobId: selectedJob.id
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Navigate to chat page with conversation ID
      navigate(`/chat?conversationId=${response.data.conversationId}`);
    } catch (error) {
      console.error('Error creating conversation:', error);
      const errorMessage = error.response?.data?.error || t('jobs.failedToApply') || 'Failed to apply to job. Please try again.';
      alert(errorMessage);
    }
  };


  if (!currentUser) {
    return (
      <div className="messages-container-page">
        <div className="loading">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={`messages-container-page ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>

      <main className="messages-main">
        <div className="job-board-layout">
          {/* Left Panel - User/Job List */}
          <div className="left-panel">
            <div className="panel-title-row">
              <h2 className="panel-title">{t(currentRole === 'business' ? 'jobs.findFreelancers' : 'jobs.findWork')}</h2>
              {currentRole === 'freelancer' && (
                <div className="mobile-section-label jobs-list-label">
                  {t('jobs.availableJobs') || 'Available Jobs'}
                </div>
              )}
            </div>

            {/* Search and Filters */}
            <div className="search-filters">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder={t('jobs.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="filters">
                <select
                  value={currentRole === 'freelancer' ? jobCategoryFilter : categoryFilter}
                  onChange={(e) => {
                    if (currentRole === 'freelancer') {
                      setJobCategoryFilter(e.target.value);
                    } else {
                      setCategoryFilter(e.target.value);
                    }
                  }}
                  className="filter-select"
                >
                  {(currentRole === 'freelancer' ? jobCategories : categories).map(cat => (
                    <option key={cat} value={cat}>
                      {cat === '__ALL__' ? t('jobs.allCategories') :
                       cat === '__OPEN_TO_ALL__' ? t('jobs.openToAll') || 'Open to All' : cat}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="filter-date"
                />
              </div>
            </div>

            {/* User/Job Cards List */}
            <div className="user-cards-list">
              {loading ? (
                <div className="loading">
                  <div className="loading-spinner"></div>
                  <p className="loading-text">{t('common.loading')}</p>
                </div>
              ) : currentRole === 'freelancer' ? (
                filteredJobs.length === 0 ? (
                  <div className="no-users-message">
                    <p>{t('jobs.noJobsFound')}</p>
                  </div>
                ) : (
                  filteredJobs.map((job) => {
                    const isSelected = selectedJob?.id === job.id;
                    const jobCategory = job.category || '';
                    const isOpenToAll = job.open_to_all || false;

                    return (
                      <div
                        key={job.id}
                        className={`user-card-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedJob(job);
                          // Scroll to job details on mobile after a short delay to ensure DOM is updated
                          setTimeout(() => {
                            if (jobDetailsRef.current) {
                              const isMobile = window.innerWidth <= 768;
                              if (isMobile) {
                                jobDetailsRef.current.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'start'
                                });
                              }
                            }
                          }, 100);
                        }}
                      >
                        <div className="user-card-content">
                          <div className="user-card-title">{job.title || t('jobs.jobTitle')}</div>
                          <div className="user-card-company">
                            {isOpenToAll ? (t('jobs.openToAll') || 'Open to All') : jobCategory}
                          </div>
                          <div className="user-card-details-row">
                            {job.location && (
                              <span className="user-card-location">
                                📍 {job.location}
                              </span>
                            )}
                            {(job.hourly_rate || job.day_rate) && (
                              <span className="user-card-rate">
                                💵 {job.hourly_rate ? `₪${job.hourly_rate}/hr` : job.day_rate ? `₪${job.day_rate}/day` : ''}
                              </span>
                            )}
                          </div>
                          {job.start_date && (
                            <div className="user-card-date-row">
                              📅 {job.start_date}
                              {job.end_date && job.end_date !== job.start_date && ` - ${job.end_date}`}
                              {job.start_time && (
                                <span> {job.start_time}
                                  {job.end_time && ` - ${job.end_time}`}
                                </span>
                              )}
                            </div>
                          )}
                          {job.expertise_level && (
                            <div className="user-card-details-row">
                              <span className="user-card-expertise">
                                🎯 {t(`jobs.expertiseLevel${job.expertise_level.charAt(0).toUpperCase() + job.expertise_level.slice(1)}`) ||
                                  job.expertise_level.charAt(0).toUpperCase() + job.expertise_level.slice(1)}
                              </span>
                            </div>
                          )}
                          {(job.mobile_required || job.equipment_required) && (
                            <div className="user-card-details-row">
                              {job.mobile_required && (
                                <span className="user-card-requirement">
                                  🚗 {t('jobs.mobileRequired') || 'Mobile Required'}
                                </span>
                              )}
                              {job.equipment_required && (
                                <span className="user-card-requirement">
                                  🛠️ {t('jobs.equipmentRequired') || 'Equipment Required'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="user-card-tag">
                          {isOpenToAll ? (t('jobs.openToAll') || 'Open to All') : jobCategory}
                        </div>
                      </div>
                    );
                  })
                )
              ) : filteredUsers.length === 0 ? (
                <div className="no-users-message">
                  <p>{t('jobs.noJobsFound')}</p>
                </div>
              ) : (
                filteredUsers.map((user) => {
                  const displayName = user.displayName || user.email?.split('@')[0] || 'Unknown';
                  const businessType = user.businessType || (currentRole === 'business' ? 'General' : t('jobs.company'));
                  const isSelected = selectedUser?.uid === user.uid;
                  const jobStartDate = user.jobStartDate || '';
                  const jobStartTime = user.jobStartTime || '';
                  const jobEndDate = user.jobEndDate || '';
                  const jobEndTime = user.jobEndTime || '';

                  // Format date for display (DD/MM/YYYY)
                  const formatJobDate = (dateString) => {
                    if (!dateString) return '';
                    const date = new Date(dateString);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${day}/${month}/${year}`;
                  };

                  // Format time for display (HH:MM)
                  const formatJobTime = (timeString) => {
                    if (!timeString) return '';
                    return timeString.substring(0, 5); // Get HH:MM from HH:MM:SS
                  };

                  // Format location for display
                  const formatLocation = (locationValue) => {
                    if (!locationValue) return t('jobs.location');
                    const locationMap = {
                      'north': t('dashboard.locationNorth'),
                      'center': t('dashboard.locationCenter'),
                      'south': t('dashboard.locationSouth')
                    };
                    return locationMap[locationValue] || locationValue;
                  };

                  return (
                    <div
                      key={user.uid}
                      className={`user-card-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleUserClick(user)}
                    >
                      <div className="user-card-content">
                        <div className="user-card-title">{user.jobTitle || displayName}</div>
                        <div
                          className="user-card-company business-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (currentRole === 'freelancer' && user.uid) {
                              navigate(`/business/${user.uid}/public`);
                            }
                          }}
                        >
                          {businessType}
                        </div>
                        <div className="user-card-details-row">
                          <span className="user-card-location">
                            📍 {currentRole === 'business'
                              ? (user.location ? formatLocation(user.location) : t('jobs.location'))
                              : formatLocation(user.jobLocation)}
                          </span>
                          {user.rate && (
                            <span className="user-card-rate">
                              💵 {user.rate}
                            </span>
                          )}
                        </div>
                        {jobStartDate && (
                          <div className="user-card-date-row">
                            📅 {t('jobs.date')} {jobStartDate}
                          </div>
                        )}
                      </div>
                      <div className="user-card-tag">{businessType}</div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right Panel - User/Job Details */}
          <div className="right-panel">
            {currentRole === 'freelancer' && selectedJob ? (
              <div className="user-details-panel" ref={jobDetailsRef}>
                <div className="mobile-job-details-header">
                  <div className="mobile-section-label selected-job-label">
                    {t('jobs.selectedJob') || 'Selected Job'}
                  </div>
                  <button
                    className="mobile-back-button"
                    onClick={() => setSelectedJob(null)}
                    aria-label={t('common.back') || 'Back'}
                  >
                    ← {t('common.back') || 'Back'}
                  </button>
                </div>
                <div className="user-details-header">
                  <div className="user-details-header-main">
                    <h3 className="job-details-title">
                      {selectedJob.title || t('jobs.jobTitle')}
                    </h3>
                    <div className="user-details-company">
                      {selectedJob.open_to_all ? (t('jobs.openToAll') || 'Open to All') : selectedJob.category || ''}
                    </div>
                  </div>
                  <button
                    className="apply-button header-apply-button"
                    onClick={() => {
                      if (selectedJob.business_id) {
                        navigate(`/business/${selectedJob.business_id}/public`);
                      }
                    }}
                  >
                    {t('jobs.viewBusiness') || 'View Business'}
                  </button>
                  <button
                    className="apply-button"
                    onClick={handleApplyToJob}
                    disabled={!selectedJob || (selectedJob.status && selectedJob.status.toLowerCase() !== 'open')}
                  >
                    {t('jobs.apply') || 'Apply'}
                  </button>
                </div>
                <div className="user-details-info">
                  {selectedJob.location && (
                    <div className="detail-info-item">
                      <span className="detail-icon">📍</span>
                      <span>{selectedJob.location}</span>
                    </div>
                  )}
                  {(selectedJob.hourly_rate || selectedJob.day_rate) && (
                    <div className="detail-info-item">
                      <span className="detail-icon">💵</span>
                      <span>
                        {selectedJob.hourly_rate ? `₪${selectedJob.hourly_rate}/hr` : ''}
                        {selectedJob.hourly_rate && selectedJob.day_rate ? ' / ' : ''}
                        {selectedJob.day_rate ? `₪${selectedJob.day_rate}/day` : ''}
                      </span>
                    </div>
                  )}
                  {selectedJob.start_date && (
                    <div className="detail-info-item">
                      <span className="detail-icon">📅</span>
                      <span>
                        {selectedJob.start_date}
                        {selectedJob.end_date && selectedJob.end_date !== selectedJob.start_date && ` - ${selectedJob.end_date}`}
                        {selectedJob.start_time && (
                          <span> {selectedJob.start_time}
                            {selectedJob.end_time && ` - ${selectedJob.end_time}`}
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {selectedJob.category && !selectedJob.open_to_all && (
                    <div className="detail-info-item">
                      <span className="detail-icon">🏷️</span>
                      <span>{selectedJob.category}</span>
                    </div>
                  )}
                  {selectedJob.expertise_level && (
                    <div className="detail-info-item">
                      <span className="detail-icon">🎯</span>
                      <span>
                        {t('jobs.expertiseLevel') || 'Expertise Level'}: {t(`jobs.expertiseLevel${selectedJob.expertise_level.charAt(0).toUpperCase() + selectedJob.expertise_level.slice(1)}`) ||
                          selectedJob.expertise_level.charAt(0).toUpperCase() + selectedJob.expertise_level.slice(1)}
                      </span>
                    </div>
                  )}
                  {selectedJob.mobile_required && (
                    <div className="detail-info-item">
                      <span className="detail-icon">🚗</span>
                      <span>{t('jobs.mobileRequired') || 'Mobile Required'}</span>
                    </div>
                  )}
                  {selectedJob.equipment_required && (
                    <div className="detail-info-item">
                      <span className="detail-icon">🛠️</span>
                      <span>{t('jobs.equipmentRequired') || 'Equipment Required'}</span>
                    </div>
                  )}
                </div>
                {selectedJob.description && (
                  <div className="detail-section">
                    <h4 className="detail-section-title">{t('jobs.description')}</h4>
                    <p className="detail-section-content">{selectedJob.description}</p>
                  </div>
                )}
                {selectedJob.required_skills && selectedJob.required_skills.length > 0 && (
                  <div className="detail-section">
                    <h4 className="detail-section-title">{t('jobs.requiredSkills') || 'Required Skills'}</h4>
                    <ul className="detail-section-list">
                      {selectedJob.required_skills.map((skill, index) => (
                        <li key={index}>{skill}</li>
                      ))}
                    </ul>
                  </div>
                )}
                  {selectedJob.required_certifications && selectedJob.required_certifications.length > 0 && (
                  <div className="detail-section">
                    <h4 className="detail-section-title">{t('jobs.requiredCertifications') || 'Required Certifications'}</h4>
                    <ul className="detail-section-list">
                      {selectedJob.required_certifications.map((cert, index) => (
                        <li key={index}>{cert}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : selectedUser ? (
              <div className="user-details-panel">
                <div className="user-details-header">
                  <div className="user-details-header-main">
                    <h3 className="job-details-title">
                      {selectedUser.jobTitle || selectedUser.displayName || selectedUser.email?.split('@')[0] || t('jobs.jobTitle')}
                    </h3>
                    <div className="user-details-company">
                      {currentRole === 'business'
                        ? (selectedUser.businessType || 'General')
                        : (selectedUser.businessType || t('jobs.company'))}
                    </div>
                  </div>
                  <button
                    className="apply-button header-apply-button"
                    onClick={() => {
                      if (!selectedUser.uid) return;
                      if (currentRole === 'freelancer') {
                        navigate(`/business/${selectedUser.uid}/public`);
                      } else if (currentRole === 'business') {
                        navigate(`/freelancer/${selectedUser.uid}/public`);
                      }
                    }}
                  >
                    {t('jobs.viewProfile') || 'View profile'}
                  </button>
                  <button
                    className="apply-button"
                    onClick={handleChatClick}
                  >
                    {currentRole === 'business' ? t('jobs.hire') : t('jobs.apply')}
                  </button>
                </div>
                <div className="user-details-info">
                  <div className="detail-info-item">
                    <span className="detail-icon">📍</span>
                    <span>{(() => {
                      // For businesses viewing freelancers, show freelancer's location
                      // For freelancers viewing jobs, show job location
                      const locationToShow = currentRole === 'business'
                        ? (selectedUser.location || selectedUser.jobLocation)
                        : selectedUser.jobLocation;

                      if (!locationToShow) return t('jobs.location');
                      const locationMap = {
                        'north': t('dashboard.locationNorth'),
                        'center': t('dashboard.locationCenter'),
                        'south': t('dashboard.locationSouth')
                      };
                      return locationMap[locationToShow] || locationToShow;
                    })()}</span>
                  </div>
                  {selectedUser.rate && (
                    <div className="detail-info-item">
                      <span className="detail-icon">💵</span>
                      <span>{selectedUser.rate}</span>
                    </div>
                  )}
                  {selectedUser.jobStartDate && (
                    <div className="detail-info-item">
                      <span className="detail-icon">📅</span>
                      <span>{(() => {
                        const date = new Date(selectedUser.jobStartDate);
                        return date.toISOString().split('T')[0];
                      })()}</span>
                    </div>
                  )}
                </div>
                {currentRole === 'business' && selectedUser.availability && (
                  <div className="detail-section">
                    <h4 className="detail-section-title">
                      {t('dashboard.currentAvailability') || 'Current availability'}
                    </h4>
                    <ul className="detail-section-list">
                      {Object.entries(selectedUser.availability).map(([dayKey, info]) => {
                        if (!info || !info.available || !info.startTime || !info.endTime) {
                          return null;
                        }
                        return (
                          <li key={dayKey}>
                            <strong>
                              {t(`dashboard.dayOfWeek.${dayKey}`) ||
                                dayKey.charAt(0).toUpperCase() + dayKey.slice(1)}
                              :
                            </strong>{' '}
                            {info.startTime} - {info.endTime}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {selectedUser.jobDescription && (
                  <div className="detail-section">
                    <h4 className="detail-section-title">{t('jobs.description')}</h4>
                    <p className="detail-section-content">{selectedUser.jobDescription}</p>
                  </div>
                )}
                {selectedUser.requirements && selectedUser.requirements.length > 0 && (
                  <div className="detail-section">
                    <h4 className="detail-section-title">{t('jobs.requirements')}</h4>
                    <ul className="detail-section-list">
                      {selectedUser.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-details-panel">
                {/* <button className="preview-button">Preview</button> */}
                <p className="empty-message">{t('jobs.selectJobToView')}</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

